import { useCallback, useEffect, useState } from 'react';

import { Typography } from '@changegps/horizon-component-library';
import { Box, Skeleton, Stack } from '@mui/material';

import { useUserInfo } from '@/features/users';
import { logger } from '@/utils';

import { useGetLicences } from '../hooks';

type LicenseType = {
  id: number;
  product: string;
  has_access: boolean;
  remaining_licences: number;
};

interface UserLicencesProps {
  loading?: boolean;
  licences?: LicenseType[];
  onSelect?: (selectedLicences: LicenseType[]) => void;
}

export const UserLicences = ({
  loading,
  licences,
  onSelect,
}: UserLicencesProps) => {
  const { company_id } = useUserInfo();
  const { data: apiLicences, isFetching } = useGetLicences(company_id, {
    enabled: !licences,
  });
  const [selectedLicences, setSelectedLicences] = useState<LicenseType[]>([]);

  // Transform API licenses to match the expected format
  const transformedLicences =
    apiLicences?.map((license) => ({
      id: license.id,
      product: license.name,
      has_access: false, // Default to false since API doesn't provide this
      remaining_licences: license.is_infinite ? Infinity : license.remaining,
    })) ?? [];

  // Use prop licenses if provided, otherwise use transformed API licenses
  const displayLicences = licences ?? transformedLicences;

  useEffect(() => {
    if (licences) {
      const newLicenses = licences.filter((license) => license.has_access);
      setSelectedLicences(newLicenses);
    }
  }, [licences]);

  useEffect(() => {
    if (onSelect) {
      onSelect(selectedLicences);
    }
  }, [selectedLicences, onSelect]);

  const handleLicenseClick = useCallback((license: LicenseType) => {
    if (license.remaining_licences === 0) {
      logger.error('No remaining licenses available');
      return;
    }

    setSelectedLicences((prev: LicenseType[]) => {
      const isAlreadySelected = prev.some((l) => l.id === license.id);
      if (isAlreadySelected) {
        return prev.filter((l) => l.id !== license.id);
      }
      return [...prev, license];
    });
  }, []);

  const isLicenseSelected = (license: LicenseType) => {
    return selectedLicences.some((l) => l.id === license.id);
  };

  const getRemainingLicences = (license: LicenseType) => {
    const isSelected = isLicenseSelected(license);

    // For API licenses (when licences prop is not provided)
    if (!licences) {
      return isSelected
        ? license.remaining_licences - 1
        : license.remaining_licences;
    }

    // For prop-based licenses
    if (license.has_access) {
      return isSelected
        ? license.remaining_licences
        : license.remaining_licences + 1;
    } else {
      return isSelected
        ? license.remaining_licences - 1
        : license.remaining_licences;
    }
  };

  const renderContent = () => {
    if (isFetching || loading) {
      return (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width={150}
              height={150}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
        }}
      >
        {displayLicences.map((license, key) => {
          const remainingCount = getRemainingLicences(license);

          return (
            <Box
              key={key}
              onClick={() => {
                handleLicenseClick(license);
              }}
              sx={{
                width: 150,
                height: 150,
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                p: 1,
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.15))',
                border:
                  license.remaining_licences === 0
                    ? '2px solid red'
                    : isLicenseSelected(license)
                      ? '2px solid #007a7a'
                      : '1px solid #ccc',
                cursor:
                  license.remaining_licences === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="body1" sx={{ color: '#111' }}>
                  {license.product}
                </Typography>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  {remainingCount === Infinity
                    ? 'Unlimited'
                    : `${remainingCount} Remaining`}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 700,
          mt: 1,
          mb: 2,
        }}
      >
        Add Licenses
      </Typography>
      {renderContent()}
    </Box>
  );
};
